import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, object=auth
  const [sessionStatus, setSessionStatus] = useState(null); // "approved" | "pending" | "revoked"
  const [error, setError] = useState("");

  // Initial check on mount
  useEffect(() => {
    const token = localStorage.getItem("celine_token");
    if (!token) {
      setUser(false);
      return;
    }
    (async () => {
      try {
        const status = await api.get("/auth/session-status");
        setSessionStatus(status.data.status);
        if (status.data.status === "approved") {
          const me = await api.get("/auth/me");
          setUser(me.data);
        } else {
          // pending/revoked: keep token but mark unauth for protected routes
          setUser(false);
        }
      } catch {
        localStorage.removeItem("celine_token");
        setSessionStatus(null);
        setUser(false);
      }
    })();
  }, []);

  // Poll session-status while pending (every 4s)
  useEffect(() => {
    if (sessionStatus !== "pending") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/auth/session-status");
        setSessionStatus(data.status);
        if (data.status === "approved") {
          const me = await api.get("/auth/me");
          setUser(me.data);
        }
      } catch {
        // ignore
      }
    }, 4000);
    return () => clearInterval(id);
  }, [sessionStatus]);

  // Poll session-status while approved (every 6s) — kicks user out instantly on revoke
  useEffect(() => {
    if (sessionStatus !== "approved") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/auth/session-status");
        if (data.status !== "approved") {
          // Revoked / pending again → force logout
          localStorage.removeItem("celine_token");
          setSessionStatus(data.status);
          setUser(false);
        }
      } catch (e) {
        // 401 (session deleted) → log out
        if (e?.response?.status === 401) {
          localStorage.removeItem("celine_token");
          setSessionStatus(null);
          setUser(false);
        }
      }
    }, 6000);
    return () => clearInterval(id);
  }, [sessionStatus]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("celine_token", data.access_token);
      setSessionStatus(data.session_status);
      if (data.session_status === "approved") {
        setUser(data.user);
      } else {
        setUser(false);
      }
      return data.session_status;
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    localStorage.removeItem("celine_token");
    setSessionStatus(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, sessionStatus, login, logout, error, setError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
